import { FIELD_CATEGORIES, EXIF_FIELDS } from './fieldDefinitions.js';

// Configuration version for future migrations
export const CONFIG_VERSION = 4;

// Default configuration - matches original 7 fields functionality
export const DEFAULT_CONFIG = {
  version: CONFIG_VERSION,
  categoryOrder: [
    FIELD_CATEGORIES.DATE_TIME,
    FIELD_CATEGORIES.LOCATION,
    FIELD_CATEGORIES.CAMERA,
    FIELD_CATEGORIES.LENS,
    FIELD_CATEGORIES.IMAGE_SETTINGS,
    FIELD_CATEGORIES.DESCRIPTIVE,
    FIELD_CATEGORIES.COPYRIGHT
  ],
  enabledFields: {
    // Default: enable common fields (similar to original 7 fields)
    dateTaken: true,
    latitude: true,
    longitude: true,
    cameraMake: true,
    cameraModel: true,
    lensModel: true,
    software: true,

    // All other fields default to disabled
    altitude: false,
    lensMake: false,
    iso: false,
    aperture: false,
    shutterSpeed: false,
    focalLength: false,
    whiteBalance: false,
    flash: false,
    orientation: false,
    description: false,
    filmStock: false,
    userComment: false,
    artist: false,
    copyright: false
  }
  // Note: presetableFields removed in v4 (all fields are now preset-eligible by design)
};

/**
 * Migrates configuration from an older version to the current version
 * @param {Object} config - Configuration to migrate
 * @returns {Object} Migrated configuration
 */
export function migrateConfig(config) {
  if (!config || typeof config.version !== 'number') {
    return DEFAULT_CONFIG;
  }

  let migrated = { ...config };

  // Migration from v1 to v2
  if (config.version === 1) {
    // Add presetableFields with defaults
    migrated = {
      ...migrated,
      presetableFields: {
        latitude: true,
        longitude: true,
        cameraMake: true,
        cameraModel: true,
        lensMake: true,
        lensModel: true,
      },
      version: 2
    };
  }

  // Migration from v2 to v3
  if (migrated.version === 2) {
    // In v3, all fields are preset-eligible by default
    // Build presetableFields with all field IDs as true
    const newPresetableFields = Object.keys(EXIF_FIELDS).reduce((acc, fieldId) => {
      acc[fieldId] = true;
      return acc;
    }, {});
    // Preserve existing presetable field settings
    if (migrated.presetableFields) {
      Object.keys(migrated.presetableFields).forEach(fieldId => {
        newPresetableFields[fieldId] = migrated.presetableFields[fieldId];
      });
    }
    migrated = {
      ...migrated,
      presetableFields: newPresetableFields,
      version: 3
    };
  }

  // Migration from v3 to v4
  if (migrated.version === 3) {
    // In v4, presetableFields is deprecated (all fields are preset-eligible by design)
    // Remove presetableFields from config to save space
    const { presetableFields, ...rest } = migrated;
    migrated = {
      ...rest,
      version: 4
    };
  }

  // If version is still behind current, return defaults
  if (migrated.version < CONFIG_VERSION) {
    return DEFAULT_CONFIG;
  }

  // If version is newer than current, return as-is (will fail validation)
  return migrated;
}

/**
 * Validates a configuration object
 * @param {Object} config - Configuration to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateConfig(config) {
  const errors = [];

  if (!config) {
    errors.push('Configuration is null or undefined');
    return { valid: false, errors };
  }

  if (typeof config !== 'object') {
    errors.push('Configuration must be an object');
    return { valid: false, errors };
  }

  // Check version
  if (!config.version || typeof config.version !== 'number') {
    errors.push('Invalid or missing version number');
  } else if (config.version > CONFIG_VERSION) {
    errors.push(`Unsupported version: ${config.version} (current version: ${CONFIG_VERSION})`);
  }

  // Validate category order
  if (!Array.isArray(config.categoryOrder)) {
    errors.push('categoryOrder must be an array');
  } else {
    const validCategories = Object.values(FIELD_CATEGORIES);
    const invalidCategories = config.categoryOrder.filter(
      cat => !validCategories.includes(cat)
    );
    if (invalidCategories.length > 0) {
      errors.push(`Invalid categories: ${invalidCategories.join(', ')}`);
    }

    // Check for duplicate categories
    const uniqueCategories = new Set(config.categoryOrder);
    if (uniqueCategories.size !== config.categoryOrder.length) {
      errors.push('categoryOrder contains duplicate categories');
    }
  }

  // Validate enabled fields
  if (!config.enabledFields || typeof config.enabledFields !== 'object') {
    errors.push('enabledFields must be an object');
  } else {
    // Check that all values are boolean
    const nonBooleanFields = Object.entries(config.enabledFields)
      .filter(([_, value]) => typeof value !== 'boolean')
      .map(([key]) => key);
    if (nonBooleanFields.length > 0) {
      errors.push(`Non-boolean field values: ${nonBooleanFields.join(', ')}`);
    }

    // Check that field IDs are valid
    const validFieldIds = Object.keys(EXIF_FIELDS);
    const invalidFieldIds = Object.keys(config.enabledFields)
      .filter(id => !validFieldIds.includes(id));
    if (invalidFieldIds.length > 0) {
      errors.push(`Unknown field IDs: ${invalidFieldIds.join(', ')}`);
    }

    // Check that at least one field is enabled
    const enabledCount = Object.values(config.enabledFields).filter(Boolean).length;
    if (enabledCount === 0) {
      errors.push('At least one field must be enabled');
    }
  }

  // Note: presetableFields validation removed in v4+ as all fields are now preset-eligible by design
  // The field may still exist in older configs for backward compatibility

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Merges partial configuration with defaults
 * Ensures all required fields exist
 * @param {Object} partialConfig - Partial configuration object
 * @returns {Object} Complete configuration with defaults filled in
 */
export function mergeWithDefaults(partialConfig) {
  if (!partialConfig || typeof partialConfig !== 'object') {
    return DEFAULT_CONFIG;
  }

  // First, migrate if needed
  const migrated = migrateConfig(partialConfig);

  return {
    version: CONFIG_VERSION,
    categoryOrder: Array.isArray(migrated.categoryOrder)
      ? migrated.categoryOrder
      : DEFAULT_CONFIG.categoryOrder,
    enabledFields: {
      ...DEFAULT_CONFIG.enabledFields,
      ...(migrated.enabledFields || {})
    }
    // Note: presetableFields removed in v4 (all fields are now preset-eligible by design)
  };
}

/**
 * Creates an export-ready configuration with metadata
 * @param {Object} config - Configuration to export
 * @returns {Object} Configuration with export metadata
 */
export function createExportConfig(config) {
  return {
    ...config,
    exportedAt: new Date().toISOString(),
    exportedBy: 'Exilftrator v2.0.0'
  };
}

/**
 * Strips export metadata from imported configuration
 * @param {Object} importedConfig - Imported configuration
 * @returns {Object} Clean configuration without metadata
 */
export function stripExportMetadata(importedConfig) {
  const { exportedAt, exportedBy, ...cleanConfig } = importedConfig;
  return cleanConfig;
}
